import React, { useState, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';

export default function TimePicker({ selectedTime, onTimeSelect, label = "Start Time" }) {
  // Parse initial time
  const parseInitialTime = () => {
    if (!selectedTime) return { hour: 9, minute: 0, period: 'AM' };
    const [hours, minutes] = selectedTime.split(':').map(Number);
    // Round minutes to nearest 15
    const roundedMinutes = Math.round(minutes / 15) * 15;
    return {
      hour: hours > 12 ? hours - 12 : hours === 0 ? 12 : hours,
      minute: roundedMinutes,
      period: hours >= 12 ? 'PM' : 'AM'
    };
  };

  const [time, setTime] = useState(parseInitialTime());

  // Generate arrays for hours (1-12), minutes (00, 15, 30, 45), and periods (AM/PM)
  const hours = Array.from({ length: 12 }, (_, i) => i + 1);
  const minutes = [0, 15, 30, 45];
  const periods = ['AM', 'PM'];

  // Update parent when time changes
  useEffect(() => {
    let hour = time.hour;
    if (time.period === 'PM' && hour !== 12) hour += 12;
    if (time.period === 'AM' && hour === 12) hour = 0;
    const timeString = `${hour.toString().padStart(2, '0')}:${time.minute.toString().padStart(2, '0')}`;
    onTimeSelect(timeString);
  }, [time]);

  const handleIncrement = (field) => {
    setTime(prev => {
      switch (field) {
        case 'hour':
          return { ...prev, hour: prev.hour === 12 ? 1 : prev.hour + 1 };
        case 'minute':
          const minuteIndex = minutes.indexOf(prev.minute);
          const nextMinute = minutes[(minuteIndex + 1) % minutes.length];
          return { ...prev, minute: nextMinute };
        case 'period':
          return { ...prev, period: prev.period === 'AM' ? 'PM' : 'AM' };
        default:
          return prev;
      }
    });
  };

  const handleDecrement = (field) => {
    setTime(prev => {
      switch (field) {
        case 'hour':
          return { ...prev, hour: prev.hour === 1 ? 12 : prev.hour - 1 };
        case 'minute':
          const minuteIndex = minutes.indexOf(prev.minute);
          const nextMinute = minutes[(minuteIndex - 1 + minutes.length) % minutes.length];
          return { ...prev, minute: nextMinute };
        case 'period':
          return { ...prev, period: prev.period === 'AM' ? 'PM' : 'AM' };
        default:
          return prev;
      }
    });
  };

  const TimeColumn = ({ value, label, onIncrement, onDecrement }) => (
    <div className="flex flex-col items-center justify-center w-20">
      <button
        onClick={onIncrement}
        className="w-full py-2 text-gray-600 hover:text-blue-600 focus:outline-none"
      >
        <svg className="w-6 h-6 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
        </svg>
      </button>
      <div className="text-2xl font-semibold text-gray-900 my-2">{value}</div>
      <button
        onClick={onDecrement}
        className="w-full py-2 text-gray-600 hover:text-blue-600 focus:outline-none"
      >
        <svg className="w-6 h-6 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
    </div>
  );

  return (
    <div className="w-full">
      {label && (
        <div className="mb-2 flex items-center">
          <span className="text-sm font-medium text-gray-700">{label}</span>
          <span className="ml-1 text-red-500">*</span>
        </div>
      )}
      
      <div className="relative bg-white rounded-lg border border-gray-200">
        <div className="px-4 py-3 text-center text-lg font-semibold text-gray-900 border-b border-gray-100">
          {time.hour}:{time.minute.toString().padStart(2, '0')} {time.period}
        </div>

        <div className="flex justify-center items-center p-4 space-x-4">
          <TimeColumn
            value={time.hour}
            label="Hour"
            onIncrement={() => handleIncrement('hour')}
            onDecrement={() => handleDecrement('hour')}
          />
          <div className="text-2xl font-semibold text-gray-900">:</div>
          <TimeColumn
            value={time.minute.toString().padStart(2, '0')}
            label="Minute"
            onIncrement={() => handleIncrement('minute')}
            onDecrement={() => handleDecrement('minute')}
          />
          <TimeColumn
            value={time.period}
            label="Period"
            onIncrement={() => handleIncrement('period')}
            onDecrement={() => handleDecrement('period')}
          />
        </div>
      </div>
    </div>
  );
}

TimePicker.propTypes = {
  selectedTime: PropTypes.string,
  onTimeSelect: PropTypes.func.isRequired,
  label: PropTypes.string
};

// Add custom styles
const styles = `
  .hide-scrollbar {
    -ms-overflow-style: none;
    scrollbar-width: none;
    -webkit-overflow-scrolling: touch;
  }
  .hide-scrollbar::-webkit-scrollbar {
    display: none;
  }
`;

const styleSheet = document.createElement("style");
styleSheet.innerText = styles;
document.head.appendChild(styleSheet); 